<template>
  <div>
    <ContentWrap title="直播配置">
      <el-alert
        title="保存后写入本机配置文件并立即生效（无需重启）。密钥留空表示不修改原值；敏感项仅脱敏展示。"
        type="info"
        :closable="false"
        class="mb16"
      />
    </ContentWrap>

    <ContentWrap title="基础直播参数" v-loading="loading">
      <el-form
        ref="runtimeFormRef"
        :model="runtimeForm"
        :rules="runtimeRules"
        label-width="160px"
        style="max-width: 720px"
      >
        <el-form-item label="是否开启直播" prop="enabled">
          <el-switch v-model="runtimeForm.enabled" active-text="开启" inactive-text="关闭" />
        </el-form-item>
        <el-form-item label="服务商">
          <el-input :model-value="config?.provider || '--'" disabled />
        </el-form-item>
        <el-form-item label="SDKAPPID" prop="sdkAppId">
          <el-input-number
            v-model="runtimeForm.sdkAppId"
            :min="1"
            :controls="false"
            class="!w-full"
            placeholder="腾讯云 IM/TRTC SDKAppID"
          />
        </el-form-item>
        <el-form-item label="SDKAPPKEY" prop="sdkAppKey">
          <el-input
            v-model="runtimeForm.sdkAppKey"
            type="password"
            show-password
            maxlength="256"
            clearable
            :placeholder="
              config?.hasSdkAppKey || config?.hasImSecretKey
                ? `已配置 ${config?.sdkAppKeyMasked || ''}，留空不修改`
                : '请输入 SDKAPPKEY'
            "
          />
        </el-form-item>
        <el-form-item label="直播BIZID" prop="bizId">
          <el-input
            v-model="runtimeForm.bizId"
            maxlength="64"
            clearable
            placeholder="如推流域名前缀数字；可留空由域名解析"
          />
        </el-form-item>
        <el-form-item label="推流防盗链Key" prop="pushAuthKey">
          <el-input
            v-model="runtimeForm.pushAuthKey"
            type="password"
            show-password
            maxlength="256"
            clearable
            :placeholder="
              config?.hasPushAuthKey || config?.hasAuthKey
                ? `已配置 ${config?.pushAuthKeyMasked || ''}，留空不修改`
                : '推流域名鉴权主 KEY'
            "
          />
        </el-form-item>
        <el-form-item label="API鉴权key" prop="apiAuthKey">
          <el-input
            v-model="runtimeForm.apiAuthKey"
            type="password"
            show-password
            maxlength="256"
            clearable
            :placeholder="
              config?.hasApiAuthKey || config?.hasCallbackKey
                ? `已配置 ${config?.apiAuthKeyMasked || ''}，留空不修改`
                : '回调/主动查询鉴权 Key'
            "
          />
        </el-form-item>
        <el-form-item label="推流域名" prop="pushDomain">
          <el-input
            v-model="runtimeForm.pushDomain"
            maxlength="255"
            clearable
            placeholder="不含协议，如 xxx.push.tlivecloud.com"
          />
        </el-form-item>
        <el-form-item label="播放域名" prop="playDomain">
          <el-input
            v-model="runtimeForm.playDomain"
            maxlength="255"
            clearable
            placeholder="不含协议，如 play.example.com"
          />
        </el-form-item>
        <el-form-item label="礼物金币转化百分比" prop="giftCoinConvertPercent">
          <el-input-number
            v-model="runtimeForm.giftCoinConvertPercent"
            :min="0"
            :max="100"
            :precision="2"
            :step="1"
            class="!w-220px"
          />
          <span class="tip">观众送礼后主播所得金币比例（0~100）</span>
        </el-form-item>
        <el-form-item label="应用名 AppName" prop="appName">
          <el-input v-model="runtimeForm.appName" maxlength="64" clearable placeholder="默认 live" />
        </el-form-item>
        <el-form-item label="鉴权过期秒数" prop="expireSeconds">
          <el-input-number
            v-model="runtimeForm.expireSeconds"
            :min="60"
            :controls="false"
            class="!w-220px"
          />
        </el-form-item>
        <el-form-item label="本地配置文件">
          <code>{{ config?.runtimeLocalConfigPath || '--' }}</code>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            :loading="runtimeSaving"
            v-hasPermi="['live:config:update']"
            @click="handleSaveRuntime"
          >
            保存并立即生效
          </el-button>
          <el-button @click="loadConfig">刷新</el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>

    <ContentWrap title="TRTC 混流转推凭证" class="mt16">
      <el-descriptions v-if="config" :column="2" border class="mb16">
        <el-descriptions-item label="TRTC 混流转推 CAM">
          <el-tag :type="config.hasTrtcCam ? 'success' : 'danger'">{{
            config.hasTrtcCam ? '已配置' : '未配置'
          }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="TRTC SecretId">
          {{ config.trtcSecretIdMasked || '--' }}
        </el-descriptions-item>
        <el-descriptions-item label="TRTC 地域">
          {{ config.trtcRegion || '--' }}
        </el-descriptions-item>
        <el-descriptions-item label="本地配置文件">
          <code>{{ config.trtcLocalConfigPath || '--' }}</code>
        </el-descriptions-item>
      </el-descriptions>

      <el-form
        ref="formRef"
        :model="trtcForm"
        :rules="withLiveInputLimits(trtcRules, liveInputLimits)"
        label-width="120px"
        style="max-width: 640px"
      >
        <el-form-item label="SecretId" prop="secretId">
          <el-input
            show-word-limit
            :maxlength="512"
            v-model="trtcForm.secretId"
            placeholder="腾讯云 CAM SecretId"
            clearable
          />
        </el-form-item>
        <el-form-item label="SecretKey" prop="secretKey">
          <el-input
            :maxlength="512"
            v-model="trtcForm.secretKey"
            type="password"
            show-password
            placeholder="腾讯云 CAM SecretKey"
            clearable
          />
        </el-form-item>
        <el-form-item label="地域" prop="region">
          <el-input
            show-word-limit
            :maxlength="64"
            v-model="trtcForm.region"
            placeholder="ap-guangzhou"
            clearable
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            :loading="saving"
            v-hasPermi="['live:config:update']"
            @click="handleSaveTrtc"
          >
            保存并立即生效
          </el-button>
          <el-button @click="loadConfig">刷新</el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>
  </div>
</template>

<script setup lang="ts">
import { withLiveInputLimits } from '@/utils/liveInputLimits'
const liveInputLimits = { secretId: 512, secretKey: 512, region: 64 }

import { ref, reactive, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { get, updateRuntime, updateTrtc, type LiveConfigVO } from '@/api/live/config'

const loading = ref(false)
const saving = ref(false)
const runtimeSaving = ref(false)
const config = ref<LiveConfigVO | null>(null)
const formRef = ref<FormInstance>()
const runtimeFormRef = ref<FormInstance>()

const runtimeForm = reactive({
  enabled: true,
  sdkAppId: undefined as number | undefined,
  sdkAppKey: '',
  bizId: '',
  pushDomain: '',
  playDomain: '',
  pushAuthKey: '',
  apiAuthKey: '',
  appName: 'live',
  expireSeconds: 21600 as number | undefined,
  giftCoinConvertPercent: 70
})

const runtimeRules: FormRules = {
  enabled: [{ required: true, message: '请设置是否开启直播', trigger: 'change' }],
  pushDomain: [{ required: true, message: '请填写推流域名', trigger: 'blur' }],
  playDomain: [{ required: true, message: '请填写播放域名', trigger: 'blur' }],
  giftCoinConvertPercent: [
    { required: true, message: '请填写礼物金币转化百分比', trigger: 'change' }
  ]
}

const trtcForm = reactive({
  secretId: '',
  secretKey: '',
  region: 'ap-guangzhou'
})
const trtcRules: FormRules = {
  secretId: [{ required: true, message: '请输入 SecretId', trigger: 'blur' }],
  secretKey: [{ required: true, message: '请输入 SecretKey', trigger: 'blur' }]
}

const fillRuntimeForm = (data: LiveConfigVO | null) => {
  if (!data) return
  runtimeForm.enabled = Boolean(data.enabled)
  runtimeForm.sdkAppId = (data.sdkAppId ?? data.imSdkAppId) as number | undefined
  runtimeForm.sdkAppKey = ''
  runtimeForm.bizId = data.bizId || ''
  runtimeForm.pushDomain = data.pushDomain || ''
  runtimeForm.playDomain = data.playDomain || ''
  runtimeForm.pushAuthKey = ''
  runtimeForm.apiAuthKey = ''
  runtimeForm.appName = data.appName || 'live'
  runtimeForm.expireSeconds = data.expireSeconds ?? 21600
  if (data.giftCoinConvertPercent != null) {
    runtimeForm.giftCoinConvertPercent = Number(data.giftCoinConvertPercent)
  } else if (data.giftAnchorShareRate != null) {
    runtimeForm.giftCoinConvertPercent = Number((Number(data.giftAnchorShareRate) * 100).toFixed(2))
  } else {
    runtimeForm.giftCoinConvertPercent = 70
  }
}

const loadConfig = async () => {
  loading.value = true
  try {
    const data: any = await get()
    config.value = data || null
    fillRuntimeForm(config.value)
    if (data?.trtcRegion) {
      trtcForm.region = data.trtcRegion
    }
  } catch (e: any) {
    config.value = null
    ElMessage.error(e?.message || '加载直播配置失败')
  } finally {
    loading.value = false
  }
}

const handleSaveRuntime = async () => {
  await runtimeFormRef.value?.validate()
  runtimeSaving.value = true
  try {
    await updateRuntime({
      enabled: runtimeForm.enabled,
      sdkAppId: runtimeForm.sdkAppId,
      sdkAppKey: runtimeForm.sdkAppKey?.trim() || undefined,
      bizId: runtimeForm.bizId?.trim() || undefined,
      pushDomain: runtimeForm.pushDomain.trim(),
      playDomain: runtimeForm.playDomain.trim(),
      pushAuthKey: runtimeForm.pushAuthKey?.trim() || undefined,
      apiAuthKey: runtimeForm.apiAuthKey?.trim() || undefined,
      appName: runtimeForm.appName?.trim() || undefined,
      expireSeconds: runtimeForm.expireSeconds,
      giftCoinConvertPercent: Number(runtimeForm.giftCoinConvertPercent)
    })
    ElMessage.success('已保存，直播配置立即生效')
    runtimeForm.sdkAppKey = ''
    runtimeForm.pushAuthKey = ''
    runtimeForm.apiAuthKey = ''
    await loadConfig()
  } catch (e: any) {
    ElMessage.error(e?.message || '保存失败')
  } finally {
    runtimeSaving.value = false
  }
}

const handleSaveTrtc = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    await updateTrtc({
      secretId: trtcForm.secretId.trim(),
      secretKey: trtcForm.secretKey.trim(),
      region: (trtcForm.region || 'ap-guangzhou').trim()
    })
    ElMessage.success('已保存，混流转推立即生效')
    trtcForm.secretKey = ''
    await loadConfig()
  } catch (e: any) {
    ElMessage.error(e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  loadConfig()
})
</script>

<style scoped>
.mb16 {
  margin-bottom: 16px;
}
.mt16 {
  margin-top: 16px;
}
.tip {
  margin-left: 12px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
}
code {
  font-size: 12px;
  word-break: break-all;
}
</style>
